import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Zap } from 'lucide-react';
import { toast } from 'react-hot-toast';
import Sidebar from '../components/Sidebar';
import CallInterface from '../components/CallInterface';
import ScriptPanel from '../components/ScriptPanel';
import FeedbackModal from '../components/FeedbackModal';
import AnalysisLoadingModal from '../components/AnalysisLoadingModal';
import MetricCard from '../components/MetricCard';
import ScenarioOnboarding from '../components/ScenarioOnboarding';
import { useCallSession } from '../hooks/useCallSession';
import api from '../services/api';

const TrainingPage = () => {
  const { scenarioId } = useParams();
  const navigate = useNavigate();
  const {
    sessionId,
    scenario,
    messages,
    isActive,
    isAiThinking,
    isReconnecting,
    isRecording,
    isProcessingMessage,
    partialTranscript,
    currentTip,
    metrics,
    objectivesProgress,
    finalAnalysis,
    isAnalyzing,
    isFinalizingSpeech,
    startCall,
    endCall,
    sendMessage,
    recordingMode,
    setRecordingMode,
    startRecording,
    stopRecording,
    toggleRecording,
  } = useCallSession();

  const [duration, setDuration] = useState(0);
  const [currentMessage, setCurrentMessage] = useState('');
  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(true);
  const [scenarioData, setScenarioData] = useState(null);

  // Load scenario data for onboarding
  useEffect(() => {
    const loadScenario = async () => {
      if (scenarioId) {
        try {
          const response = await api.get(`/api/scenarios/${scenarioId}`);
          setScenarioData(response.data.scenario);
        } catch (error) {
          console.error('Error loading scenario:', error);
          toast.error('Ошибка загрузки сценария');
        }
      }
    };
    loadScenario();
  }, [scenarioId]);

  // Handle onboarding completion
  const handleOnboardingComplete = () => {
    setShowOnboarding(false);
    if (scenarioId && !sessionId && !isActive) {
      startCall(scenarioId);
    }
  };

  // Update duration timer
  useEffect(() => {
    let interval;
    if (isActive) {
      interval = setInterval(() => {
        setDuration((prev) => prev + 1000);
      }, 1000);
    } else {
      setDuration(0);
    }
    return () => clearInterval(interval);
  }, [isActive]);

  // Update current message display
  useEffect(() => {
    if (partialTranscript) {
      setCurrentMessage(partialTranscript);
    } else if (messages.length > 0) {
      const lastMessage = messages[messages.length - 1];
      setCurrentMessage(lastMessage.content);
    }
  }, [messages, partialTranscript]);

  const handleEndCall = () => {
    endCall();
  };

  // Show feedback modal when finalAnalysis is available
  useEffect(() => {
    if (finalAnalysis && !isActive) {
      setIsFeedbackOpen(true);
    }
  }, [finalAnalysis, isActive]);

  return (
    <div className="bg-slate-50 text-slate-800 h-screen overflow-hidden flex">
      <Sidebar />

      {/* Onboarding Modal */}
      {showOnboarding && scenarioData && (
        <ScenarioOnboarding
          scenario={scenarioData}
          onComplete={handleOnboardingComplete}
        />
      )}

      <main className="md:ml-64 flex-1 flex flex-col h-screen relative">
        {/* Top Header */}
        <header className="h-16 border-b border-slate-200 bg-white/80 backdrop-blur-sm flex items-center justify-between px-6 shrink-0 z-10">
          <div className="flex items-center gap-4">
            <span className="text-slate-400 text-sm">Тренировка</span>
            <span className="text-slate-300 text-sm">/</span>
            <h1 className="text-sm font-medium text-slate-900 tracking-tight">
              {scenario?.name || 'Загрузка...'}
            </h1>
            {scenario && (
              <span className="px-2 py-0.5 rounded-full bg-blue-50 text-blue-600 text-xs font-medium border border-blue-100">
                Сценарий #{scenario.id}
              </span>
            )}
          </div>

          <div className="flex items-center gap-4">
            {isActive && (
              <div className="flex items-center gap-2 text-xs text-green-600 bg-green-50 px-3 py-1.5 rounded-full border border-green-100">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
                </span>
                AI Активен
              </div>
            )}
          </div>
        </header>

        {/* Analysis Loading Modal */}
        <AnalysisLoadingModal isOpen={isAnalyzing} />

        {/* Feedback Modal */}
        <FeedbackModal
          isOpen={isFeedbackOpen}
          onClose={() => {
            setIsFeedbackOpen(false);
            navigate('/scenarios');
          }}
          analysis={finalAnalysis}
          scenario={scenario}
        />

        {/* Workspace Grid */}
        <div className="flex-1 p-6 grid grid-cols-1 lg:grid-cols-12 gap-6 min-h-0 overflow-hidden">
          {/* Left Column: Call Interface */}
          <div className="lg:col-span-7 flex flex-col gap-6 min-h-0">
            <CallInterface
              character={scenario?.character}
              isActive={isActive}
              isRecording={isRecording}
              isAiThinking={isAiThinking}
              isReconnecting={isReconnecting}
              isProcessingMessage={isProcessingMessage}
              isFinalizingSpeech={isFinalizingSpeech}
              currentMessage={currentMessage}
              duration={duration}
              recordingMode={recordingMode}
              onRecordingModeChange={setRecordingMode}
              onStartRecording={startRecording}
              onStopRecording={stopRecording}
              onToggleRecording={toggleRecording}
              onEndCall={handleEndCall}
            />

            {/* Real-time Analysis Metrics */}
            <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
              <div className="flex items-center gap-2 mb-3">
                <div className="p-1.5 bg-yellow-50 rounded-lg text-yellow-600">
                  <Zap className="w-4 h-4" />
                </div>
                <h3 className="text-sm font-medium text-slate-900">
                  Анализ в реальном времени
                </h3>
              </div>

              {metrics ? (
                <div className="grid grid-cols-3 gap-3">
                  <MetricCard
                    label="Вовлеченность"
                    value={metrics.engagement}
                    unit="%"
                    description="Показывает насколько сбалансирован диалог между вами и собеседником. Чем ближе к 100%, тем более равномерный обмен репликами. Избегайте монологов!"
                  />
                  <MetricCard
                    label="Прогресс"
                    value={metrics.progress}
                    unit="%"
                    description="Процент выполненных целей звонка. AI автоматически отслеживает достижение целей на основе контекста диалога. 100% = все цели достигнуты."
                  />
                  <MetricCard
                    label="Темп"
                    value={metrics.pace}
                    unit="/мин"
                    description="Количество сообщений в минуту. Оптимальный темп: 2-4 сообщения/мин. Слишком быстро = нет времени на обдумывание. Слишком медленно = затянутый диалог."
                  />
                </div>
              ) : (
                <p className="text-xs text-slate-500">
                  {isActive
                    ? 'Метрики появятся после первого сообщения'
                    : 'Начните звонок для отслеживания метрик'}
                </p>
              )}
            </div>
          </div>

          {/* Right Column: Script & Tools */}
          <div className="lg:col-span-5 min-h-0 flex">
            <ScriptPanel
              scenario={scenario}
              messages={messages}
              onSendMessage={sendMessage}
              currentTip={currentTip}
              objectivesProgress={objectivesProgress}
            />
          </div>
        </div>
      </main>
    </div>
  );
};

export default TrainingPage;
